import { useRef } from "react";

import type { ScheduleRouteOption } from "../data/routes";
import type { ShiftTemplateDraft } from "../data/schedule-templates";
import { ShiftDurationPicker } from "../components/ShiftDurationPicker";
import { TimezoneSelect } from "../components/TimezoneSelect";
import { WeekdayPicker } from "../components/WeekdayPicker";
import {
  COLOR_TOKENS,
  COLOR_TOKEN_COPY,
  OPEN_FILL_MODES,
  OPEN_FILL_MODE_COPY,
  OPEN_FILL_MODE_HELP,
  type ColorToken,
} from "../../../../packages/shared/src/schedule-color.ts";

// The one Define-a-shift field cluster, shared by the Templates drawer form and the
// connected wizard's Define step (Wave 2 Slice C). Both write through the SAME hosted
// create/update RPCs, so the fields they show must be the same fields; extracting them is
// what keeps the two surfaces from drifting into two vocabularies for one draft shape.
// Pure presentation: state stays with the caller, submission stays with the caller.

/**
 * Contract gap 1 (6B design doc): the 6A contract defines pending cleanup on approve/full,
 * but says nothing about pending requests when a supervisor flips an existing template back
 * to instant claim. The form WARNS and proceeds - it does not promise a cleanup the server
 * has not documented.
 *
 * The design doc calls those pendings "unreachable by every frozen RPC"; the SHIPPED
 * migration is narrower. `approve_open_shift_request` refuses them with the claim-only
 * sentence (migration line 1930), but `reject_open_shift_request` has no mode gate, so they
 * stay rejectable. The copy states exactly that, because a supervisor who is told the
 * requests are stranded would leave them sitting instead of clearing them.
 */
export const MODE_FLIP_WARNING =
  "Switching back to instant claim does not cancel requests that are already pending. They can still be rejected, but they can no longer be approved.";

/** Roving-tabindex arrow keys for the swatch radiogroup (APG radio pattern). */
function nextTokenIndex(key: string, index: number, count: number): number | null {
  if (key === "ArrowRight" || key === "ArrowDown") return (index + 1) % count;
  if (key === "ArrowLeft" || key === "ArrowUp") return (index - 1 + count) % count;
  if (key === "Home") return 0;
  if (key === "End") return count - 1;
  return null;
}

/** 12 frozen tokens as a radiogroup. Every swatch carries its token name as text. */
function ColorTokenPicker({
  value,
  onChange,
  disabled,
}: {
  value: ColorToken;
  onChange: (token: ColorToken) => void;
  disabled: boolean;
}) {
  const refs = useRef<Array<HTMLButtonElement | null>>([]);
  const selectedIndex = Math.max(0, COLOR_TOKENS.indexOf(value));
  return (
    <div className="tpl__swatches" role="radiogroup" aria-label="Template color">
      {COLOR_TOKENS.map((token, index) => (
        <button
          key={token}
          ref={(node) => {
            refs.current[index] = node;
          }}
          type="button"
          role="radio"
          aria-checked={token === value}
          tabIndex={index === selectedIndex ? 0 : -1}
          className={`tpl__swatch tpl-color--${token}`}
          disabled={disabled}
          onClick={() => onChange(token)}
          onKeyDown={(event) => {
            const target = nextTokenIndex(event.key, index, COLOR_TOKENS.length);
            if (target === null) return;
            event.preventDefault();
            onChange(COLOR_TOKENS[target]!);
            refs.current[target]?.focus();
          }}
        >
          <span className="tpl__swatch-dot" aria-hidden="true" />
          {COLOR_TOKEN_COPY[token]}
        </button>
      ))}
    </div>
  );
}

export function TemplateDraftFields({
  draft,
  routes,
  busy,
  onPatch,
  showModeFlipWarning = false,
}: {
  draft: ShiftTemplateDraft;
  routes: readonly ScheduleRouteOption[];
  busy: boolean;
  onPatch: (partial: Partial<ShiftTemplateDraft>) => void;
  /** True only when an EDIT moves an existing request-approval template back to instant claim. */
  showModeFlipWarning?: boolean;
}) {
  function pickRoute(routeId: string): void {
    const route = routes.find((candidate) => candidate.routeId === routeId);
    onPatch({ routeId, siteId: route?.siteId ?? "" });
  }

  return (
    <>
      <label className="tpl__field">
        Route
        <select value={draft.routeId} onChange={(event) => pickRoute(event.target.value)} required>
          <option value="">Choose a route...</option>
          {routes.map((route) => (
            <option key={route.routeId} value={route.routeId}>
              {route.routeName} - {route.siteName}
            </option>
          ))}
        </select>
      </label>
      <label className="tpl__field">
        Name
        <input
          type="text"
          value={draft.name}
          onChange={(event) => onPatch({ name: event.target.value })}
          required
        />
      </label>
      <div className="tpl__field-row tpl__field-row--split">
        <label className="tpl__field">
          Start time
          <input
            type="time"
            value={draft.localStartTime}
            onChange={(event) => onPatch({ localStartTime: event.target.value })}
            required
          />
        </label>
        <label className="tpl__field">
          Guards needed
          <input
            type="number"
            min={1}
            value={draft.headcountRequired}
            onChange={(event) => onPatch({ headcountRequired: Number(event.target.value) || 1 })}
            required
          />
        </label>
      </div>
      <ShiftDurationPicker
        durationMinutes={draft.durationMinutes}
        localStartTime={draft.localStartTime}
        onChange={(durationMinutes) => onPatch({ durationMinutes })}
      />
      <fieldset className="tpl__fieldset">
        <legend className="tpl__legend">Weekdays</legend>
        <p className="setup__field-hint">
          Which days this template repeats. Tap <strong>All</strong> for seven-day coverage, or
          pick individual days.
        </p>
        <WeekdayPicker value={draft.weekdays} onChange={(weekdays) => onPatch({ weekdays })} />
      </fieldset>
      <div className="tpl__field-row">
        <label className="tpl__field">
          Active from
          <input
            type="date"
            value={draft.activeFrom}
            onChange={(event) => onPatch({ activeFrom: event.target.value })}
            required
          />
        </label>
        <label className="tpl__field">
          Active until (optional)
          <input
            type="date"
            value={draft.activeUntil ?? ""}
            onChange={(event) =>
              onPatch({ activeUntil: event.target.value === "" ? null : event.target.value })
            }
          />
        </label>
        <label className="tpl__field">
          Site timezone
          <TimezoneSelect
            value={draft.siteTimezone}
            onChange={(next) => onPatch({ siteTimezone: next })}
            required
          />
        </label>
      </div>
      <fieldset className="tpl__fieldset">
        <legend className="tpl__legend">Color</legend>
        <p className="setup__field-hint">
          A label for this shift type on the coverage board and the guard apps. It never
          changes a slot's status color.
        </p>
        <ColorTokenPicker
          value={draft.colorToken}
          onChange={(colorToken) => onPatch({ colorToken })}
          disabled={busy}
        />
      </fieldset>
      <fieldset className="tpl__fieldset">
        <legend className="tpl__legend">Open shifts</legend>
        <div className="tpl__segmented" role="group" aria-label="Open fill mode">
          {OPEN_FILL_MODES.map((mode) => (
            <button
              key={mode}
              type="button"
              aria-pressed={draft.openFillMode === mode}
              disabled={busy}
              onClick={() => onPatch({ openFillMode: mode })}
            >
              {OPEN_FILL_MODE_COPY[mode]}
            </button>
          ))}
        </div>
        <p className="tpl__help">{OPEN_FILL_MODE_HELP[draft.openFillMode]}</p>
        {showModeFlipWarning ? (
          <p className="tpl__callout" role="status">
            {MODE_FLIP_WARNING}
          </p>
        ) : null}
      </fieldset>
    </>
  );
}
